// Migrate existing localStorage data to Supabase on first login
// Maps old string IDs to new UUIDs, preserving cross-references

import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { toDb } from '@/lib/mappers';

const STORAGE_KEY = 'kuadra2-storage';
const BACKUP_KEY = 'kuadra2-storage-backup';

export async function migrateLocalData(userId) {
  if (!isSupabaseConfigured()) return;

  // Check if localStorage has data
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return;

  let stored;
  try {
    stored = JSON.parse(raw);
  } catch {
    return;
  }

  const state = stored?.state;
  if (!state) return;

  // Check if there's actually data to migrate (beyond defaults)
  const hasData = (state.transactions?.length > 0) ||
    (state.budgets?.length > 0) ||
    (state.goals?.length > 0);

  if (!hasData) {
    await markMigrated(userId);
    return;
  }

  // Check if Supabase already has data (avoid double migration)
  const { data: existingTx } = await supabase
    .from('transactions')
    .select('id')
    .eq('user_id', userId)
    .limit(1);

  if (existingTx?.length > 0) {
    await markMigrated(userId);
    return;
  }

  // ID mapping: old string ID → new UUID
  const idMap = new Map();

  function newId(oldId) {
    if (!oldId) return null;
    if (!idMap.has(oldId)) {
      idMap.set(oldId, crypto.randomUUID());
    }
    return idMap.get(oldId);
  }

  try {
    // 1. Members
    if (state.members?.length) {
      const rows = state.members.map((m) => ({
        id: newId(m.id),
        user_id: userId,
        name: m.name,
        relationship: m.relationship,
        initials: m.initials,
        color: m.color,
        emoji: m.emoji,
        photo_url: m.photoUrl || m.photo || null,
      }));
      await supabase.from('members').insert(rows);
    }

    // 2. Banks
    if (state.banks?.length) {
      const rows = state.banks.map((b) => ({
        id: newId(b.id),
        user_id: userId,
        name: b.name,
        color: b.color,
      }));
      await supabase.from('banks').insert(rows);
    }

    // 3. Businesses
    if (state.businesses?.length) {
      const rows = state.businesses.map((b) => ({
        id: newId(b.id),
        user_id: userId,
        name: b.name,
        icon: b.icon,
        color: b.color,
        description: b.description,
        currency: b.currency,
      }));
      await supabase.from('businesses').insert(rows);
    }

    // 4. Income Units (without fixed costs first)
    if (state.incomeUnits?.length) {
      const rows = state.incomeUnits.map((u) => ({
        id: newId(u.id),
        user_id: userId,
        name: u.name,
        gross_income: u.grossIncome,
        currency: u.currency,
        member_ids: (u.memberIds || (u.memberId ? [u.memberId] : [])).map((id) => newId(id)),
        color: u.color,
        icon: u.icon,
        recurrence: u.recurrence,
        month: u.month,
      }));
      await supabase.from('income_units').insert(rows);

      // 5. Fixed Costs
      const fixedCosts = [];
      for (const u of state.incomeUnits) {
        if (!u.fixedCosts?.length) continue;
        for (const fc of u.fixedCosts) {
          fixedCosts.push({
            id: newId(fc.id),
            user_id: userId,
            income_unit_id: newId(u.id),
            name: fc.name,
            amount: fc.amount,
          });
        }
      }
      if (fixedCosts.length) {
        await supabase.from('fixed_costs').insert(fixedCosts);
      }
    }

    // 6. Transactions (first pass without linked_to to avoid FK issues)
    if (state.transactions?.length) {
      // Pre-generate all IDs
      state.transactions.forEach((tx) => newId(tx.id));

      const rows = state.transactions.map((tx) => ({
        id: newId(tx.id),
        user_id: userId,
        description: tx.description,
        amount: tx.amount,
        type: tx.type,
        category: tx.category,
        date: tx.date,
        linked_to: null, // Will update in second pass
        member_ids: (tx.memberIds || (tx.memberId ? [tx.memberId] : [])).map((id) => newId(id)),
        unit_id: tx.unitId ? newId(tx.unitId) : null,
        business_id: tx.businessId ? newId(tx.businessId) : null,
        paid: tx.paid !== undefined ? tx.paid : true,
      }));
      await supabase.from('transactions').insert(rows);

      // Second pass: update linked_to references
      const linkedUpdates = state.transactions
        .filter((tx) => tx.linkedTo)
        .map((tx) => ({
          id: newId(tx.id),
          linked_to: newId(tx.linkedTo),
        }));

      for (const update of linkedUpdates) {
        await supabase
          .from('transactions')
          .update({ linked_to: update.linked_to })
          .eq('id', update.id);
      }
    }

    // 7. Budgets
    if (state.budgets?.length) {
      const rows = state.budgets.map((b) => ({
        id: newId(b.id),
        user_id: userId,
        category: b.category,
        limit: b.limit,
        spent: b.spent,
      }));
      await supabase.from('budgets').insert(rows);
    }

    // 8. Goals
    if (state.goals?.length) {
      const rows = state.goals.map((g) => ({
        id: newId(g.id),
        user_id: userId,
        name: g.name,
        target: g.target,
        saved: g.saved,
        icon: g.icon,
      }));
      await supabase.from('goals').insert(rows);
    }

    // 9. Splits
    if (state.splits?.length) {
      const rows = state.splits.map((s) => ({
        id: newId(s.id),
        user_id: userId,
        description: s.description,
        amount: s.amount,
        my_share: s.myShare,
        partner_share: s.partnerShare,
        date: s.date,
      }));
      await supabase.from('splits').insert(rows);
    }

    // 10. Credits
    if (state.credits?.length) {
      const rows = state.credits.map((c) => ({
        id: newId(c.id),
        user_id: userId,
        bank_id: c.bankId ? newId(c.bankId) : null,
        type: c.type,
        description: c.description,
        total_amount: c.totalAmount,
        installment_amount: c.installmentAmount,
        total_installments: c.totalInstallments,
        paid_installments: c.paidInstallments,
        start_date: c.startDate,
        category: c.category,
      }));
      await supabase.from('credits').insert(rows);
    }

    // 11. Agenda Items
    if (state.agendaItems?.length) {
      // Pre-generate IDs for agenda items
      state.agendaItems.forEach((a) => newId(a.id));

      const rows = state.agendaItems.map((a) => ({
        id: newId(a.id),
        user_id: userId,
        title: a.title,
        amount: a.amount,
        type: a.type,
        category: a.category,
        date: a.date,
        recurrence: a.recurrence,
        icon: a.icon,
        color: a.color,
        notes: a.notes,
        paid: a.paid,
        transaction_id: a.transactionId ? newId(a.transactionId) : null,
      }));
      await supabase.from('agenda_items').insert(rows);
    }

    // 12. App Settings
    if (state.appSettings) {
      await supabase.from('app_settings').upsert({
        user_id: userId,
        chat_enabled: state.appSettings.chatEnabled ?? true,
        business_enabled: state.appSettings.businessEnabled ?? false,
        scan_enabled: state.appSettings.scanEnabled ?? true,
      }, { onConflict: 'user_id' });
    }

    // 13. Update profile with user data from store
    if (state.user) {
      await supabase.from('profiles').update({
        name: state.user.name,
        email: state.user.email,
        plan: state.user.plan,
        currency: state.user.currency,
        initials: state.user.initials,
      }).eq('id', userId);
    }

    // Mark as migrated
    await markMigrated(userId);

    // Backup localStorage and remove original
    localStorage.setItem(BACKUP_KEY, raw);
    localStorage.removeItem(STORAGE_KEY);

  } catch (err) {
    console.error('Data migration failed:', err);
    throw err;
  }
}

async function markMigrated(userId) {
  await supabase
    .from('profiles')
    .update({ migrated_from_local: true })
    .eq('id', userId);
}
